
import { animate, timeline, inView } from 'https://cdn.jsdelivr.net/npm/motion@10.18.0/+esm';

// Helper functions
function throttle(callback) {
    let requestId = null, lastArgs;
    const later = (context) => () => {
        requestId = null;
        callback.apply(context, lastArgs);
    };
    const throttled = (...args) => {
        lastArgs = args;
        if (requestId === null) {
            requestId = requestAnimationFrame(later(this));
        }
    };
    throttled.cancel = () => {
        cancelAnimationFrame(requestId);
        requestId = null;
    };
    return throttled;
}

function debounce(fn, delay) {
    let timer = null;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            fn.apply(this, args);
        }, delay);
    };
}

class Delegate {
    constructor(element) {
        this.element = element;
        this.controllers = new Set();
    }
    on(type, selector, handler) {
        const controller = new AbortController();
        this.controllers.add(controller);
        this.element.addEventListener(type, (event) => {
            const target = event.target.closest(selector);
            if (target && this.element.contains(target)) {
                handler.call(this.element, event, target);
            }
        }, { signal: controller.signal });
    }
    destroy() {
        this.controllers.forEach(c => c.abort());
        this.controllers.clear();
    }
}

class GestureArea {
    constructor(element, callbacks) {
        this.element = element;
        this.callbacks = callbacks || {};
        this.startX = 0;
        this.startY = 0;

        this.element.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.element.addEventListener('touchend', this.onTouchEnd.bind(this));
    }

    onTouchStart(e) {
        this.startX = e.changedTouches[0].screenX;
        this.startY = e.changedTouches[0].screenY;
    }

    onTouchEnd(e) {
        const endX = e.changedTouches[0].screenX;
        const endY = e.changedTouches[0].screenY;

        const diffX = endX - this.startX;
        const diffY = endY - this.startY;

        // Threshold of 50px for swipe
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
            if (diffX > 0) {
                if (this.callbacks.onSwipeRight) this.callbacks.onSwipeRight();
            } else {
                if (this.callbacks.onSwipeLeft) this.callbacks.onSwipeLeft();
            }
        }
    }
}

class Player extends EventTarget {
    constructor(durationInSec, stopOnVisibility = true) {
        super();
        this.duration = durationInSec * 1000;
        this.remainingTime = this.duration;
        this.state = "paused";
        this.timer = null;
        this.startTime = null;

        this.callback = () => this.dispatchEvent(new CustomEvent("player:end"));

        if (stopOnVisibility) {
            document.addEventListener("visibilitychange", () => {
                if (document.hidden) {
                    this.pause();
                    this.dispatchEvent(new CustomEvent("player:visibility-pause"));
                } else {
                    this.resume();
                    this.dispatchEvent(new CustomEvent("player:visibility-resume"));
                }
            });
        }
    }

    pause() {
        if (this.state === "started") {
            clearTimeout(this.timer);
            this.state = "paused";
            this.remainingTime -= (Date.now() - this.startTime);
            this.dispatchEvent(new CustomEvent("player:pause"));
        }
    }

    resume(restartTimer = false) {
        if (this.state !== "stopped") {
            if (restartTimer) {
                this.start();
            } else {
                clearTimeout(this.timer);
                this.startTime = Date.now();
                this.state = "started";
                this.timer = setTimeout(this.callback, this.remainingTime);
                this.dispatchEvent(new CustomEvent("player:resume"));
            }
        }
    }

    start() {
        clearTimeout(this.timer);
        this.startTime = Date.now();
        this.state = "started";
        this.remainingTime = this.duration;
        this.timer = setTimeout(this.callback, this.remainingTime);
        this.dispatchEvent(new CustomEvent("player:start"));
    }

    stop() {
        clearTimeout(this.timer);
        this.state = "stopped";
        this.dispatchEvent(new CustomEvent("player:stop"));
    }
}

class EffectCarousel extends HTMLElement {
    constructor() {
        super();
        this.targetIndex = 0;
        this.preventInitialTransition = false;
        this.player = null;
    }

    connectedCallback() {
        this.targetIndex = Math.max(0, this.cells.findIndex(item => item.classList.contains("is-selected")));
        if (this.targetIndex === -1) this.targetIndex = 0;

        // Initial setup
        this.cells.forEach((cell, index) => {
            cell.classList.toggle("is-selected", index === this.targetIndex);
        });

        inView(this, () => this.onBecameVisible());

        if (this.hasAttribute('allow-swipe')) {
            new GestureArea(this, {
                onSwipeLeft: () => this.next(),
                onSwipeRight: () => this.previous()
            });
        }
    }

    get allowSwipe() { return this.hasAttribute("allow-swipe"); }
    get cellSelector() { return this.getAttribute("cell-selector"); }
    get allCells() { return this.cellSelector ? Array.from(this.querySelectorAll(this.cellSelector)) : Array.from(this.children); }
    get cells() { return this.allCells.filter(cell => !cell.hasAttribute("hidden")); }
    get selectedCell() { return this.cells[this.selectedIndex]; }
    get selectedIndex() { return this.targetIndex; }

    previous({ instant = false } = {}) {
        return this.select((this.selectedIndex - 1 + this.cells.length) % this.cells.length, { instant, direction: "previous" });
    }

    next({ instant = false } = {}) {
        return this.select((this.selectedIndex + 1) % this.cells.length, { instant, direction: "next" });
    }

    async select(index, { instant = false, direction = null } = {}) {
        if (index === this.selectedIndex) return Promise.resolve();

        this.player?.pause();
        const fromSlide = this.selectedCell;
        const toSlide = this.cells[index];
        direction = direction ?? (index > this.selectedIndex ? "next" : "previous");

        this.targetIndex = index;
        this.dispatchEvent(new CustomEvent("carousel:change", { detail: { index, cell: toSlide } }));

        const animationControls = this.createOnChangeAnimationControls(fromSlide, toSlide, { direction });

        if (animationControls.leaveControls && animationControls.enterControls) {
            const leave = animationControls.leaveControls();
            if (instant) leave.finish();
            await leave.finished;

            this.player?.resume(true);
            fromSlide.classList.remove("is-selected");
            toSlide.classList.add("is-selected");

            const enter = animationControls.enterControls();
            if (instant) enter.finish();
            await enter.finished;
        } else {
            if (instant) animationControls.finish();
            this.player?.resume(true);
            toSlide.classList.add("is-selected");
            await animationControls.finished;
            fromSlide.classList.remove("is-selected");
        }

        this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index, cell: toSlide } }));
    }

    async onBecameVisible() {
        const animationControls = await this.createOnBecameVisibleAnimationControls(this.selectedCell);
        if (this.preventInitialTransition && animationControls.finish) animationControls.finish();

        animationControls.finished.then(() => {
            this.player?.resume(true);
            this.dispatchEvent(new CustomEvent("carousel:settle", { detail: { index: this.selectedIndex, cell: this.selectedCell } }));
        });
    }

    createOnBecameVisibleAnimationControls(toSlide) {
        return animate(toSlide, {}, { duration: 0 });
    }

    createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
        return timeline([
            [fromSlide, { opacity: [1, 0] }, { duration: 0.3 }],
            [toSlide, { opacity: [0, 1] }, { duration: 0.3, at: "<" }]
        ]);
    }
}

class SlideshowCarousel extends EffectCarousel {
    constructor() {
        super();
        this.delegate = new Delegate(this);

        this.delegate.on("click", '[data-action="navigate-next"]', () => this.next());

        if (this.hasAttribute("autoplay")) {
            const duration = parseFloat(this.getAttribute("autoplay-duration") || "5");
            this.player = new Player(duration);
            this.player.addEventListener("player:end", () => this.next());
        }
    }

    disconnectedCallback() {
        this.delegate.destroy();
        this.player?.stop();
    }

    async createOnBecameVisibleAnimationControls(toSlide) {
        return super.createOnBecameVisibleAnimationControls(toSlide);
    }

    createOnChangeAnimationControls(fromSlide, toSlide, { direction } = {}) {
        return {
            leaveControls: () => animate(fromSlide, { opacity: 0, transform: "scale(1.05)" }, { duration: 0.5 }),
            enterControls: () => {
                const ctrl = animate(toSlide, { opacity: [0, 1], transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.5 });
                ctrl.finished.then(() => {
                    // Clear inline styles left by Motion so CSS takes over
                    toSlide.style.removeProperty('opacity');
                    toSlide.style.removeProperty('transform');
                    fromSlide.style.removeProperty('opacity');
                    fromSlide.style.removeProperty('transform');
                });
                return ctrl;
            }
        };
    }
}

class CarouselNavigation extends HTMLElement {
    constructor() {
        super();
        this.abortController = new AbortController();
    }

    connectedCallback() {
        this.carousel = document.getElementById(this.getAttribute("aria-controls"));
        if (!this.carousel) return;

        this.buttons = Array.from(this.querySelectorAll("button"));
        this.buttons.forEach((btn, index) => {
            btn.addEventListener("click", () => {
                this.carousel.select(index);
            }, { signal: this.abortController.signal });
        });

        this.carousel.addEventListener("carousel:change", (e) => {
            this.updateState(e.detail.index);
        }, { signal: this.abortController.signal });

        // Initial state
        this.updateState(this.carousel.selectedIndex);
    }

    disconnectedCallback() {
        this.abortController.abort();
    }

    updateState(index) {
        this.buttons.forEach((btn, i) => {
            btn.setAttribute("aria-current", i === index ? "true" : "false");
        });
    }
}

class CarouselPrevButton extends HTMLElement {
    connectedCallback() {
        this.carousel = document.getElementById(this.getAttribute("aria-controls"));
        if (this.carousel) {
            this.addEventListener("click", () => this.carousel.previous());
        }
    }
}

class CarouselNextButton extends HTMLElement {
    connectedCallback() {
        this.carousel = document.getElementById(this.getAttribute("aria-controls"));
        if (this.carousel) {
            this.addEventListener("click", () => this.carousel.next());
        }
    }
}

// Register Custom Elements (only if not already registered)
if (!customElements.get('effect-carousel')) {
    customElements.define('effect-carousel', EffectCarousel);
}
if (!customElements.get('slideshow-carousel')) {
    customElements.define('slideshow-carousel', SlideshowCarousel);
}
if (!customElements.get('carousel-navigation')) {
    customElements.define('carousel-navigation', CarouselNavigation);
}
if (!customElements.get('carousel-prev-button')) {
    customElements.define('carousel-prev-button', CarouselPrevButton);
}
if (!customElements.get('carousel-next-button')) {
    customElements.define('carousel-next-button', CarouselNextButton);
}
